import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AvatarMedium } from '../../components';


// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { MARKETPLACE_TZ } from '../../util/dates';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionReviews from './SectionReviews';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  












  const [mounted, setMounted] = useState(false);

// Destructure props
const {
  isAuthenticated,
  currentUser,
  getListing,
  getOwnListing,
  intl,
  onManageDisableScrolling,
  params: rawParams,
  location,
  scrollingDisabled,
  showListingError,
  reviews = [],
  fetchReviewsError,
  sendInquiryInProgress,
  sendInquiryError,
  history,
  callSetInitialValues,
  onSendInquiry,
  onInitializeCardPaymentData,
  config,
  routeConfiguration,
  showOwnListingsOnly,
  monthlyTimeSlots,
  ...restOfProps
} = props;

// Define listing-related constants
const listingConfig = config.listing;
const listingId = new UUID(rawParams.id);
const isVariant = rawParams.variant != null;
const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

const currentListing =
  isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
    ? ensureOwnListing(getOwnListing(listingId))
    : ensureListing(getListing(listingId));

// Debug log (safe now)
console.log('LOCAL currentListing publicData:', currentListing?.attributes?.publicData);

useEffect(() => {
  if (
    currentListing?.id &&
    currentListing?.attributes?.availabilityPlan &&
    props.onFetchTimeSlots
  ) {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    // Listings without an `availabilityPlan` (drafts, or any listing whose
    // plan was never persisted) would TypeError on this line under the old
    // non-optional access. Day-shape plans (Sherbrt's marketplace default)
    // also have no `timezone` key, so we fall back to MARKETPLACE_TZ — the
    // same fallback every other availability code path uses, to keep
    // fetch and render aligned on a single calendar-day basis.
    const timeZone =
      currentListing?.attributes?.availabilityPlan?.timezone || MARKETPLACE_TZ;

    console.log('🚀 Fetching time slots on mount:', {
      listingId: currentListing.id.uuid,
      start,
      end,
      timeZone,
    });

    props.onFetchTimeSlots(currentListing.id.uuid, start, end, timeZone);
  }
}, [currentListing?.id?.uuid]); // ✅ only reruns when listing changes






// ✅ Set mounted flag for use elsewhere
useEffect(() => {
  setMounted(true);
}, []);

// Listing route params
const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
const params = { slug: listingSlug, ...rawParams };

const listingPathParamType = isDraftVariant
  ? LISTING_PAGE_PARAM_TYPE_DRAFT
  : LISTING_PAGE_PARAM_TYPE_EDIT;
const listingTab = isDraftVariant ? 'photos' : 'details';

const isApproved =
  currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

const pendingIsApproved = isPendingApprovalVariant && isApproved;










  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;
  
  // Debug log to inspect retail price
  console.log('Listing data:', {
    price,
    publicData,
    retailPrice: publicData.retailPrice,
    hasRetailPrice: !!publicData.retailPrice
  });
  



  // Resolve the brand's display label from the listing field config (hosted
  // enumOptions), so we show the configured label (e.g. "AFRM") rather than a
  // title-cased version of the stored option value (e.g. "Afrm"). Falls back to
  // a title-cased value only if no matching option/label is configured.
  const brandFieldConfig = config?.listing?.listingFields?.find(f => f.key === 'brand');
  const brandOption = brandFieldConfig?.enumOptions?.find(o => `${o.option}` === publicData?.brand);
  const brandLabel =
    brandOption?.label ||
    (publicData?.brand
      ? publicData.brand.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : '');

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase ? 'purchase' : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && (processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected);
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);
  const formattedRetailPrice = publicData.retailPrice
    ? intl.formatNumber(publicData.retailPrice, {
        style: 'currency',
        currency: config.currency,
      })
    : null;

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    '${config.layout.listingImage.variantPrefix}-2x'
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = '${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}';
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

    const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

    const author = ensureUser(currentListing.author);
    const rawAuthorDisplayName = author.attributes.profile.displayName;
    const authorPublicData = author.attributes.profile.publicData || {};
    console.log('authorPublicData:', authorPublicData);
    const authorAbbreviatedName = `${rawAuthorDisplayName.split(' ')[0]} ${rawAuthorDisplayName.split(' ')[1]?.charAt(0) || ''}.`;

    // Formatting helpers
    const formatHeight = h => h ? h.replace('_', "'") + '"' : '—';
    const formatDressSizeLetter = l => l ? l.replace('_', '/').toUpperCase() : '—';
    const formatDressSizeNumber = n => n ? n.replace('_', '/') : '—';

    // Measurements section
    const measurements = (
      <div className={css.measurements}>
        <h3 className={css.measurementsTitle}>Details</h3>
        <ul className={css.measurementsList}>
          <li><strong>Height:</strong> {formatHeight(authorPublicData.height)}</li>
          <li><strong>Bra Size:</strong> {authorPublicData.bra_size || '—'}</li>
          <li><strong>Dress Size Number:</strong> {formatDressSizeNumber(authorPublicData.dress_size_number)}</li>
          <li><strong>Dress Size Letter:</strong> {formatDressSizeLetter(authorPublicData.dress_size_letter)}</li>
          <li><strong>Jeans Size:</strong> {authorPublicData.jeans_size || '—'}</li>
        </ul>
      </div>
    );

    const authorSection = (
      <section className={css.sectionAuthor}>
        <h2 className={css.sectionTitle}>About the lender</h2>
        <div className={css.authorCard}>
          <AvatarMedium className={css.avatar} user={author} />
          <div className={css.authorDetails}>
            <p>Hello, I'm {authorAbbreviatedName}</p>
            <NamedLink name="ProfilePage" params={{ id: author.id.uuid }}>
              View profile
            </NamedLink>
            {measurements}
          </div>
        </div>
      </section>
    );
  
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }}
    >
<LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
  <div className={css.contentWrapperForProductLayout}>
    <div className={css.mainColumnForProductLayout}>
      {mounted && currentListing.id && noPayoutDetailsSetWithOwnListing ? (
        <ActionBarMaybe
          className={css.actionBarForProductLayout}
          isOwnListing={isOwnListing}
          listing={currentListing}
          showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
          currentUser={currentUser}
        />
      ) : null}
      {mounted && currentListing.id ? (
        <ActionBarMaybe
          className={css.actionBarForProductLayout}
          isOwnListing={isOwnListing}
          listing={currentListing}
          currentUser={currentUser}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
        />
      ) : null}
      <SectionGallery
        listing={currentListing}
        variantPrefix={config.layout.listingImage.variantPrefix}
      />
        {authorSection}

    </div>
    <div className={css.orderColumnForProductLayout}>
    <div className={css.productInfoSection}>
  <div className={css.brandLink}>
    {publicData?.brand ? (
      <NamedLink
        name="SearchPage"
        to={{
          search: `?pub_category=dresses&pub_brand=${encodeURIComponent(publicData.brand)}`
        }}
        className={css.brandLinkText}
      >
        {brandLabel}
      </NamedLink>
    ) : null}
  </div>

  <H4 as="h1" className={css.listingTitle}>{title}</H4>

  {/* Price + Retail Price */}
  <div className={css.pricingRow}>
    <span className={css.boldPrice}>{formattedPrice}</span>
    {formattedRetailPrice ? (
      <span className={css.retailPrice}>
        {formattedRetailPrice}
      </span>
    ) : null}
  </div>

  {/* Description */}
  <p className={css.descriptionText}>{description}</p>

  {/* Size + Color */}
  <p className={css.detailText}>Size: {publicData.size}</p>
  <p className={css.detailText}>Color: {publicData.color}</p>

  {/* Chat link */}
  <p className={css.inquiryPrompt}>
    <em>
      Questions on style or fit?{' '}
      <a href="mailto:bestie@shoponsherbrt.com" className={css.inquiryLink}>Just ask!</a>
    </em>
  </p>
</div>


      <OrderPanel
        className={css.productOrderPanel}
        listing={currentListing}
        isOwnListing={isOwnListing}
        onSubmit={handleOrderSubmit}
        titleDesktop={null}
        payoutDetailsWarning={payoutDetailsWarning}
        author={ensuredAuthor}
        onManageDisableScrolling={onManageDisableScrolling}
        onContactUser={onContactUser}
        hidePrice={true}
        {...restOfProps}
        validListingTypes={config.listing.listingTypes}
        marketplaceCurrency={config.currency}
        dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
        marketplaceName={config.marketplaceName}
        monthlyTimeSlots={monthlyTimeSlots} 
      />
    </div>
  </div>
</LayoutSingleColumn>



    </Page>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: '${location.pathname}${location.search}${location.hash}' }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedListingPage);

export default ListingPage;